import { CssBaseline } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { Analytics } from "@vercel/analytics/react";
import parse from "html-react-parser";
import App from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useMemo, useState } from "react";
import * as gtag from "../lib/google/gtag";
import AuthStateChanged from "../src/AuthStateChanged";
import GrufityProgress from "../src/components/_common/GrufityProgress";
import ErrorBoundary from "../src/components/ErrorBoundary";
import Layout from "../src/components/Layout";
import { initFirebase } from "../src/config/firebase.config";
import { AuthProvider } from "../src/hook/auth";
import "../src/static/scrollbar.css";
import CookieConsent from "../src/utils/CookieConsent";
import "../src/utils/CookieConsent/dist/dist.css";
import { getTheme, GrufityTheme } from "../src/utils/theme";

initFirebase();

if (typeof window !== "undefined" || typeof global !== "undefined") {
  if (!Array.prototype.includes) {
    Object.defineProperty(Array.prototype, "includes", {
      enumerable: false,
      value: function (obj) {
        //  return this.filter(el => el === obj).length > 0;
        return this.indexOf(obj) !== -1;
      },
    });
  }

  if (!String.prototype.includes) {
    Object.defineProperty(String.prototype, "includes", {
      enumerable: false,
      value: function (...args) {
        return String.prototype.indexOf.apply(this, args) !== -1;
      },
    });
  }

  if (!String.prototype.toCapitalize) {
    Object.defineProperty(String.prototype, "toCapitalize", {
      enumerable: false,
      value: function () {
        if (!this) return this;
        return this.split(" ")
          .map((d) => (d.length > 0 ? d[0].toUpperCase() + d.substring(1) : d))
          .join(" ");
      },
    });
  }

  if (!Array.prototype.equals) {
    Object.defineProperty(Array.prototype, "equals", {
      enumerable: false,
      value: function (array) {
        if (!array || this.length !== array.length) return false;
        for (let i = 0, l = this.length; i < l; i += 1) {
          if (this[i] instanceof Array && array[i] instanceof Array) {
            if (!this[i].equals(array[i])) return false;
          } else if (this[i] !== array[i]) {
            return false;
          }
        }
        return true;
      },
    });
  }

  if (!Array.prototype.max) {
    Object.defineProperty(Array.prototype, "max", {
      enumerable: false,
      value: function (prop) {
        // return this.reduce((item, d) => {
        //   if (prop) return item = item[prop] && d[prop] <= item[prop] ? item : d;
        //   return item = item && d <= item ? item : d;
        // }, prop ? {} : undefined);
        if (this.length === 0) return undefined;
        return this.reduce((acc, curr) => {
          if (prop) return curr[prop] > acc[prop] ? curr : acc;
          return curr > acc ? curr : acc;
        });
      },
    });
  }

  if (!Array.prototype.min) {
    Object.defineProperty(Array.prototype, "min", {
      enumerable: false,
      value: function (prop) {
        // return this.reduce((item, d) => {
        //   if (prop) return item = item[prop] && d[prop] >= item[prop] ? item : d;
        //   return item = item && d >= item ? item : d;
        // }, prop ? {} : undefined);
        if (this.length === 0) return undefined;
        return this.reduce((acc, curr) => {
          if (prop) return curr[prop] < acc[prop] ? curr : acc;
          return curr < acc ? curr : acc;
        });
      },
    });
  }

  if (!Array.prototype.mid) {
    Object.defineProperty(Array.prototype, "mid", {
      enumerable: false,
      value: function (call) {
        const arrWithoutZeros = this.filter((value) => value !== 0);
        if (arrWithoutZeros.length === 0) return undefined;
        const sortedArr = arrWithoutZeros.sort((a, b) =>
          call ? call(a) - call(b) : a - b
        );
        const midIndex = Math.round(sortedArr.length / 2);
        return sortedArr[midIndex - 1];
      },
    });
  }
}

function MyApp({ Component, pageProps }) {
  const router = useRouter();

  const [themeMode, setThemeMode] = useState("light");
  const [pageLoading, setPageLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [cookieConsent, setCookieConsent] = useState({});

  const theme = useMemo(() => createTheme(getTheme(themeMode)), [themeMode]);
  const shouldShowAd = router.asPath?.includes("blogs");

  const toggleColorMode = () => {
    setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  const handleLoadingState = () => {
    setPageLoading(false);
  };

  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url, true);
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    // Clean up server-side injected CSS for legacy MUI configurations
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles && jssStyles.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }

    setHistory((prevHistory) => {
      if (prevHistory[prevHistory.length - 1] !== router.asPath) {
        // Retain max 50 history states to prevent memory leaks in SPA navigation
        return [...prevHistory, router.asPath].slice(-50);
      }
      return prevHistory;
    });
  }, [router.asPath]);

  useEffect(() => {
    const hours = 24;
    const now = new Date().getTime();
    const setupTime = localStorage.getItem("setupTime");

    if (!setupTime) {
      localStorage.setItem("setupTime", now.toString());
    } else if (now - parseInt(setupTime, 10) > hours * 60 * 60 * 1000) {
      // NOTE: localStorage.clear() removes ALL keys. Ensure this behavior is intended 
      // and doesn't unexpectedly purge auth states or other domain-level configurations.
      localStorage.clear();
      localStorage.setItem("setupTime", now.toString());
    }
  }, []);

  return (
    <>
      <GrufityTheme>
        <Head>
          <meta charSet="utf-8" />
          <meta name="msvalidate.01" content="909F6353FF854D82141A5DDC12212820" />
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
          />
          <meta name="theme-color" content={theme.palette.primary.main} />
          {pageProps?.meta_tags ? (
            parse(pageProps.meta_tags)
          ) : (
            <title>Grufity</title>
          )}
        </Head>
        <CssBaseline />

        <AuthProvider>
          <Layout
            toggleColorMode={toggleColorMode}
            themeMode={themeMode}
            pageLoading={pageLoading}
          >
            <ErrorBoundary
              history={history}
              FallbackComponent={
                <GrufityProgress
                  color="#ffffff"
                  startPosition={0.3}
                  stopDelayMs={200}
                  height={3}
                  showOnShallow={true}
                  options={{ easing: "ease", speed: 500, trickleSpeed: 200 }}
                />
              }
            >
              <AuthStateChanged
                setpageLoading={handleLoadingState}
                pageLoading={pageLoading}
              >
                <Component
                  {...pageProps}
                  themeMode={themeMode}
                  pageLoading={pageLoading}
                  history={history}
                />

                <Analytics
                  beforeSend={(event) => {
                    if (event.url.includes("dev")) return null;
                    return event;
                  }}
                />

                <CookieConsent setConsent={setCookieConsent} />
              </AuthStateChanged>
            </ErrorBoundary>

            <GrufityProgress
              color="#ffffff"
              startPosition={0.3}
              stopDelayMs={200}
              height={3}
              showOnShallow={true}
              options={{
                easing: "ease",
                speed: 500,
                trickleSpeed: 200,
                showSpinner: true,
              }}
            />
          </Layout>
        </AuthProvider>
      </GrufityTheme>

      <Script
        strategy="afterInteractive"
        src="https://www.googletagmanager.com/gtag/js?id=G-N5ZXKR5D3T"
      />

      {shouldShowAd && (
        <Script
          strategy="lazyOnload"
          crossOrigin="anonymous"
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9822821274784800"
        />
      )}

      <Script
        id="google-tag"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-N5ZXKR5D3T');
          `,
        }}
      />
    </>
  );
}

MyApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      pathname: appContext.ctx.asPath,
    },
  };
};

export default MyApp;